import { useState } from 'react'

import { useAction } from '../components/toast'
import { Badge, Card, Kpi, Loaded, Modal, Notice, Page, Table } from '../components/ui'
import { del, get, post, put } from '../lib/api'
import { P, useAuth } from '../lib/auth'
import { count, percent, relative, titleCase } from '../lib/format'
import { useResource } from '../lib/useResource'

type Status = {
  environment: string
  database: string
  ai_error_rate_1h: number
  ai_requests_1h: number
  queue_depths: Record<string, number>
  providers_configured: Record<string, boolean>
  catalog_models: number
}

type Credential = {
  provider: string
  configured: boolean
  /** 'console' | 'environment' | 'unset' — which layer is actually in force. */
  source: string
  base_url: string
  supports_base_url: boolean
  environment_fallback: boolean
  updated_by: string | null
  updated_at: string | null
}

export function System() {
  const status = useResource((signal) => get<Status>('/v1/admin/system', undefined, signal))
  const { can } = useAuth()

  return (
    <Page
      title="System"
      subtitle="Service health as the API itself reports it."
      actions={
        <button className="btn btn--sm" onClick={status.reload}>
          Refresh
        </button>
      }
    >
      <Loaded resource={status}>
        {(data) => (
          <>
            {data.environment === 'prod' && (
              <Notice tone="warn">
                This console is pointed at <strong>production</strong>. Everything you do here
                affects real accounts and real medical records.
              </Notice>
            )}

            <div className="grid grid--kpi">
              <Kpi
                label="Environment"
                value={titleCase(data.environment)}
                tone={data.environment === 'prod' ? 'alert' : 'cyan'}
              />
              <Kpi
                label="Database"
                value={titleCase(data.database)}
                tone={data.database === 'ok' ? 'green' : 'alert'}
              />
              <Kpi label="AI requests · 1 h" value={count(data.ai_requests_1h)} />
              <Kpi
                label="AI error rate · 1 h"
                value={percent(data.ai_error_rate_1h)}
                tone={data.ai_error_rate_1h > 0.05 ? 'alert' : 'green'}
                foot="Gateway failures over gateway attempts"
              />
              <Kpi label="Models in catalog" value={count(data.catalog_models)} />
            </div>

            <div className="grid grid--2">
              {can(P.aiCredentialWrite) ? (
                <>
                  <ProviderCredentials onChanged={status.reload} />
                  <IntegrationCredentials />
                  <PushTest />
                </>
              ) : (
                <Card
                  title="Provider credentials"
                  hint="Whether a key is configured — never the key, and never a prefix of it"
                  flush
                >
                  <Table
                    rows={Object.entries(data.providers_configured).map(
                      ([provider, configured]) => ({ provider, configured }),
                    )}
                    rowKey={(row) => row.provider}
                    empty="No providers configured."
                    columns={[
                      {
                        key: 'p',
                        header: 'Provider',
                        strong: true,
                        render: (row) => titleCase(row.provider),
                      },
                      {
                        key: 'state',
                        header: 'Key',
                        render: (row) =>
                          row.configured ? (
                            <Badge tone="green">Configured</Badge>
                          ) : (
                            <Badge>Absent — routing skips this provider</Badge>
                          ),
                      },
                    ]}
                  />
                </Card>
              )}

              <Card title="Queue depths" flush>
                <Table
                  rows={Object.entries(data.queue_depths).map(([queue, depth]) => ({ queue, depth }))}
                  rowKey={(row) => row.queue}
                  empty="No queues reporting."
                  columns={[
                    { key: 'q', header: 'Queue', strong: true, render: (row) => titleCase(row.queue) },
                    {
                      key: 'd',
                      header: 'Depth',
                      render: (row) => (
                        <span className="num" style={{ color: row.depth > 0 ? 'var(--orange)' : undefined }}>
                          {count(row.depth)}
                        </span>
                      ),
                    },
                  ]}
                />
              </Card>
            </div>

            <Card title="What this screen does not tell you">
              <Notice>
                Migration state, background-job history and release metadata are not reported here
                because nothing in this deployment currently records them. Rather than render an
                empty panel that looks like a healthy one, the screen omits them. Wiring them up
                means having Alembic report{' '}
                <span className="mono">current</span> through an endpoint and having the job runner
                write its runs to a table — until then, check{' '}
                <span className="mono">alembic current</span> on the host.
              </Notice>
            </Card>
          </>
        )}
      </Loaded>
    </Page>
  )
}

/**
 * Provider keys, editable.
 *
 * The list is deliberately write-only: a key can be set or cleared, never read
 * back, because no endpoint returns one. What an operator gets instead is
 * provenance — whether the value in force came from this console or from the
 * deployment's environment, who set it, and when. That is enough to answer "is
 * this the key I think it is?" without the console ever holding a secret it
 * could leak.
 */
function ProviderCredentials({ onChanged }: { onChanged: () => void }) {
  const credentials = useResource((signal) =>
    get<Credential[]>('/v1/admin/ai/credentials', undefined, signal),
  )
  const run = useAction()
  const [editing, setEditing] = useState<Credential | null>(null)
  const [clearing, setClearing] = useState<Credential | null>(null)

  const reload = () => {
    credentials.reload()
    onChanged()
  }

  return (
    <Card
      title="Provider credentials"
      hint="Set or rotate a key. Keys are never displayed — not even a prefix"
      flush
    >
      <Loaded resource={credentials}>
        {(rows) => (
          <>
            <Table
              rows={rows}
              rowKey={(row) => row.provider}
              empty="No providers."
              columns={[
                {
                  key: 'p',
                  header: 'Provider',
                  strong: true,
                  render: (row) => titleCase(row.provider),
                },
                {
                  key: 'state',
                  header: 'Key',
                  render: (row) =>
                    row.source === 'console' ? (
                      <Badge tone="green">Set here</Badge>
                    ) : row.source === 'environment' ? (
                      <Badge tone="cyan">From environment</Badge>
                    ) : (
                      <Badge>Absent — routing skips this provider</Badge>
                    ),
                },
                {
                  key: 'when',
                  header: 'Last set',
                  render: (row) =>
                    row.updated_at ? (
                      <span className="dim">{relative(row.updated_at)}</span>
                    ) : (
                      <span className="dim">—</span>
                    ),
                },
                {
                  key: 'act',
                  header: '',
                  render: (row) => (
                    <span className="row row--end" style={{ gap: 6 }}>
                      <button className="btn btn--sm" onClick={() => setEditing(row)}>
                        {row.source === 'console' ? 'Rotate' : 'Set key'}
                      </button>
                      {row.source === 'console' && (
                        <button className="btn btn--sm btn--ghost" onClick={() => setClearing(row)}>
                          Clear
                        </button>
                      )}
                    </span>
                  ),
                },
              ]}
            />
            <Notice>
              A key set here is encrypted before it is stored and takes effect on the next request —
              no restart. Clearing one falls back to the deployment's environment variable, which is
              what rolling back a bad rotation looks like.
            </Notice>
          </>
        )}
      </Loaded>

      {editing && (
        <CredentialEditor
          credential={editing}
          onClose={() => setEditing(null)}
          onSaved={reload}
        />
      )}

      {clearing && (
        <Modal title={`Clear the ${titleCase(clearing.provider)} key`} onClose={() => setClearing(null)}>
          <ClearForm
            credential={clearing}
            onConfirm={(reason) =>
              run(
                () =>
                  del(`/v1/admin/ai/credentials/${clearing.provider}`, { reason }),
                `${titleCase(clearing.provider)} key cleared.`,
                () => {
                  setClearing(null)
                  reload()
                },
              )
            }
            onClose={() => setClearing(null)}
          />
        </Modal>
      )}
    </Card>
  )
}

/** Human names for the integration slugs, and what each key is for. */
const INTEGRATION_LABELS: Record<string, { name: string; hint: string }> = {
  fcm: {
    name: 'Firebase (push)',
    hint: 'The whole service-account JSON file, not just a key.',
  },
  weather: {
    name: 'Weather',
    hint: 'QWeather or Seniverse API key. Pushed to a paired band so it can show the local forecast.',
  },
}

/**
 * Service credentials that are not AI providers.
 *
 * The endpoint has existed since Firebase push was added, but nothing in the
 * console ever called it — so the only way to set these was an environment
 * variable and a redeploy. Same storage, same audit trail, same
 * never-readable-back rule as the provider keys above.
 */
type PushTestResult = {
  configured: boolean
  project_id: string
  devices: number
  sent: number
  failed: number
  detail: string
}

/** Sends a fixed test notification, to prove an FCM key actually delivers.
 *
 *  The endpoint has existed since the key rotation but nothing could reach it,
 *  so the only way to know a new Firebase key worked was to wait for something
 *  real to fire — which means either deleting the old key on faith or leaving a
 *  leaked one live. The body is fixed; this cannot be made to send chosen text.
 */
function PushTest() {
  const { can } = useAuth()
  const run = useAction()
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<PushTestResult | null>(null)

  if (!can(P.configWrite)) return null

  const send = async () => {
    setBusy(true)
    await run(
      async () => {
        const out = await post<PushTestResult>('/v1/admin/integrations/push-test', {
          email: email.trim(),
        })
        setResult(out)
      },
      'Test sent.',
    )
    setBusy(false)
  }

  return (
    <Card
      title="Test push delivery"
      hint="Proves a Firebase key works before you delete the one it replaces"
    >
      <div className="stack" style={{ padding: 16 }}>
        <div className="field">
          <label className="field__label" htmlFor="push-email">
            Send to (leave blank for your own devices)
          </label>
          <input
            id="push-email"
            className="input"
            value={email}
            placeholder="the account signed in on the handset"
            onChange={(event) => setEmail(event.target.value)}
          />
          <p className="muted" style={{ margin: '4px 0 0' }}>
            The person must have signed in on the phone at least once — the push token
            registers at sign-in.
          </p>
        </div>

        <div className="row row--end">
          <button className="btn btn--sm btn--primary" disabled={busy} onClick={() => void send()}>
            {busy ? 'Sending…' : 'Send test notification'}
          </button>
        </div>

        {result && (
          <Notice tone={result.sent > 0 ? undefined : 'warn'}>
            <div className="stack" style={{ gap: 4 }}>
              <div>{result.detail}</div>
              <div className="dim" style={{ fontSize: 11.5 }}>
                project <span className="mono">{result.project_id || '—'}</span> ·{' '}
                {result.devices} device{result.devices === 1 ? '' : 's'} · {result.sent} sent ·{' '}
                {result.failed} failed
              </div>
              {/* The project id is the check that catches the subtle failure: a
                  valid key for the wrong Firebase project authenticates fine
                  and delivers to nobody. */}
            </div>
          </Notice>
        )}
      </div>
    </Card>
  )
}

function IntegrationCredentials() {
  const credentials = useResource((signal) =>
    get<Credential[]>('/v1/admin/integrations/credentials', undefined, signal),
  )
  const [editing, setEditing] = useState<Credential | null>(null)

  return (
    <Card
      title="Service credentials"
      hint="Push and weather. Keys are never displayed — not even a prefix"
      flush
    >
      <Loaded resource={credentials}>
        {(rows) => (
          <>
            <Table
              rows={rows}
              rowKey={(row) => row.provider}
              empty="No integrations."
              columns={[
                {
                  key: 'p',
                  header: 'Service',
                  strong: true,
                  render: (row) =>
                    INTEGRATION_LABELS[row.provider]?.name ?? titleCase(row.provider),
                },
                {
                  key: 'state',
                  header: 'Key',
                  render: (row) =>
                    row.source === 'console' ? (
                      <Badge tone="green">Set here</Badge>
                    ) : row.source === 'environment' ? (
                      <Badge tone="cyan">From environment</Badge>
                    ) : (
                      <Badge>Not set</Badge>
                    ),
                },
                {
                  key: 'when',
                  header: 'Last set',
                  render: (row) =>
                    row.updated_at ? (
                      <span className="dim">{relative(row.updated_at)}</span>
                    ) : (
                      <span className="dim">—</span>
                    ),
                },
                {
                  key: 'act',
                  header: '',
                  render: (row) => (
                    <span className="row row--end">
                      <button className="btn btn--sm" onClick={() => setEditing(row)}>
                        {row.source === 'console' ? 'Rotate' : 'Set key'}
                      </button>
                    </span>
                  ),
                },
              ]}
            />
            <Notice>
              Without a weather key the band shows no forecast; everything else keeps working.
              Without Firebase, emergency dispatch cannot send a push.
            </Notice>
          </>
        )}
      </Loaded>

      {editing && (
        <IntegrationEditor
          credential={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            credentials.reload()
          }}
        />
      )}
    </Card>
  )
}

function IntegrationEditor({
  credential,
  onClose,
  onSaved,
}: {
  credential: Credential
  onClose: () => void
  onSaved: () => void
}) {
  const run = useAction()
  const [apiKey, setApiKey] = useState('')
  const [reason, setReason] = useState('')
  const meta = INTEGRATION_LABELS[credential.provider]
  // The same minimums the API enforces, so Save explains itself rather than
  // returning a 400 after the operator has pasted a secret.
  const canSave = apiKey.trim().length >= 8 && reason.trim().length >= 10

  return (
    <Modal title={`Set the ${meta?.name ?? credential.provider} key`} onClose={onClose}>
      <div className="stack">
        {meta && <p className="dim" style={{ margin: 0, fontSize: 12.5 }}>{meta.hint}</p>}

        <div className="field">
          <label className="field__label" htmlFor="int-key">
            Key
          </label>
          <textarea
            id="int-key"
            className="input"
            rows={credential.provider === 'fcm' ? 6 : 2}
            value={apiKey}
            onChange={(event) => setApiKey(event.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="int-reason">
            Reason (recorded in the audit log, minimum 10 characters)
          </label>
          <input
            id="int-reason"
            className="input"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>

        <button
          className="btn btn--primary"
          disabled={!canSave}
          onClick={() =>
            run(
              () =>
                put(`/v1/admin/integrations/credentials/${credential.provider}`, {
                  api_key: apiKey.trim(),
                  reason: reason.trim(),
                }),
              'Key saved.',
              onSaved,
            )
          }
        >
          Save
        </button>
      </div>
    </Modal>
  )
}

function CredentialEditor({
  credential,
  onClose,
  onSaved,
}: {
  credential: Credential
  onClose: () => void
  onSaved: () => void
}) {
  const run = useAction()
  const [apiKey, setApiKey] = useState('')
  const [baseUrl, setBaseUrl] = useState(credential.base_url)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  // The same rules the API enforces, so the operator sees why Save is disabled
  // instead of getting a 400 after pasting a key.
  const needsBaseUrl = credential.supports_base_url && !baseUrl.trim()
  const canSave = apiKey.trim().length >= 8 && reason.trim().length >= 10 && !needsBaseUrl

  const save = async () => {
    setBusy(true)
    await run(
      () =>
        put(`/v1/admin/ai/credentials/${credential.provider}`, {
          api_key: apiKey.trim(),
          base_url: credential.supports_base_url ? baseUrl.trim() : '',
          reason: reason.trim(),
        }),
      `${titleCase(credential.provider)} key saved.`,
      () => {
        onSaved()
        onClose()
      },
    )
    setBusy(false)
  }

  return (
    <Modal title={`${titleCase(credential.provider)} API key`} onClose={onClose}>
      <div className="stack">
        {credential.source === 'console' && (
          <Notice>
            This replaces the key currently in force. The old one is not recoverable from here —
            the console has never held a readable copy of it.
          </Notice>
        )}
        {credential.source === 'environment' && (
          <Notice tone="warn">
            This provider currently uses the key from the deployment's environment. Saving here
            overrides it until the row is cleared.
          </Notice>
        )}

        <div className="field">
          <label className="field__label" htmlFor="api-key">
            API key
          </label>
          <input
            id="api-key"
            className="input"
            type="password"
            autoComplete="off"
            spellCheck={false}
            value={apiKey}
            autoFocus
            onChange={(event) => setApiKey(event.target.value)}
            placeholder="Paste the key — it is written once and never read back"
          />
        </div>

        {credential.supports_base_url && (
          <div className="field">
            <label className="field__label" htmlFor="base-url">
              Endpoint base URL
            </label>
            <input
              id="base-url"
              className="input"
              value={baseUrl}
              spellCheck={false}
              onChange={(event) => setBaseUrl(event.target.value)}
              placeholder="https://your-endpoint/v1"
            />
            <p className="muted" style={{ margin: '4px 0 0' }}>
              Must be https, or loopback for a model server on this machine. Only the custom
              provider has a settable endpoint — the vendor URLs are fixed in code.
            </p>
          </div>
        )}

        <div className="field">
          <label className="field__label" htmlFor="cred-reason">
            Reason (recorded in the audit log, minimum 10 characters)
          </label>
          <textarea
            id="cred-reason"
            className="textarea"
            style={{ minHeight: 72 }}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Ticket reference and what prompted this rotation"
          />
        </div>

        <div className="row row--end">
          <button className="btn btn--ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn--primary" disabled={busy || !canSave} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save key'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

function ClearForm({
  credential,
  onConfirm,
  onClose,
}: {
  credential: Credential
  onConfirm: (reason: string) => Promise<void> | void
  onClose: () => void
}) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  return (
    <div className="stack">
      <Notice tone={credential.environment_fallback ? undefined : 'warn'}>
        {credential.environment_fallback
          ? 'The environment still holds a key for this provider, so it takes over immediately.'
          : 'Nothing else holds a key for this provider. Clearing it stops routing to it until a new key is set.'}
      </Notice>
      <div className="field">
        <label className="field__label" htmlFor="clear-reason">
          Reason (recorded in the audit log, minimum 10 characters)
        </label>
        <textarea
          id="clear-reason"
          className="textarea"
          style={{ minHeight: 72 }}
          value={reason}
          autoFocus
          onChange={(event) => setReason(event.target.value)}
        />
      </div>
      <div className="row row--end">
        <button className="btn btn--ghost" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button
          className="btn btn--danger"
          disabled={busy || reason.trim().length < 10}
          onClick={() => {
            setBusy(true)
            void Promise.resolve(onConfirm(reason.trim())).finally(() => setBusy(false))
          }}
        >
          {busy ? 'Working…' : 'Clear key'}
        </button>
      </div>
    </div>
  )
}
